import { FC } from 'react';
import { useNavigate } from 'react-router-dom';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box, Popover, Typography, useTheme } from '@mui/material';

import { SideMenuItem } from './config';
import { ActiveIndicator } from './styled';

interface Props {
  anchorEl: HTMLElement | null;
  title: string;
  submenus: SideMenuItem[];
  isActiveRoute: (menuItem: SideMenuItem) => boolean;
  onClose: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

const LABEL_SX = {
  color: 'grey.800',
  fontFamily: 'Neue Frutiger World',
  fontSize: '0.8125rem',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: 1.6,
  whiteSpace: 'nowrap' as const,
};

const SettingsHoverPopover: FC<Props> = ({
  anchorEl,
  title,
  submenus,
  isActiveRoute,
  onClose,
  onMouseEnter,
  onMouseLeave,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();

  const visibleSubmenus = submenus.filter((s) => !s.hidden);

  const handleNavigate = (path: string) => {
    navigate(path);
    onClose();
  };

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      disableRestoreFocus
      disableAutoFocus
      disableEnforceFocus
      sx={{ pointerEvents: 'none' }}
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: '0.75rem',
          bgcolor: '#fefefe',
          boxShadow: '0px 1px 8px 0px rgba(0, 0, 0, 0.15)',
          ml: '1rem',
          pointerEvents: 'auto',
          minWidth: '14.75rem',
        },
        onMouseEnter,
        onMouseLeave,
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', p: '0.5rem', gap: '0.25rem' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            px: '0.375rem',
            py: '0.25rem',
            mb: '0.25rem',
          }}
        >
          <KeyboardArrowDownIcon sx={{ fontSize: '1rem', color: '#615E6F' }} />
          <Typography sx={LABEL_SX}>{title}</Typography>
        </Box>

        {visibleSubmenus.map((item) => {
          const isActive = isActiveRoute(item);
          return (
            <Box
              key={item.label}
              onClick={() => item.path && handleNavigate(item.path)}
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                gap: '0.62rem',
                px: '0.62rem',
                py: '0.62rem',
                borderRadius: '0.75rem',
                cursor: item.path ? 'pointer' : 'default',
                backgroundColor: isActive ? theme.palette.accent.light : 'transparent',
                '&:hover': {
                  backgroundColor: isActive ? theme.palette.accent.light : theme.palette.grey[50],
                },
                '& svg': {
                  color: isActive ? theme.palette.accent.main : theme.palette.grey[700],
                  width: '1.25rem',
                  height: '1.25rem',
                  flexShrink: 0,
                },
              }}
            >
              {isActive && <ActiveIndicator />}
              {item.icon}
              <Typography
                sx={{
                  ...LABEL_SX,
                  color: isActive ? theme.palette.accent.main : 'grey.800',
                }}
              >
                {item.label}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Popover>
  );
};

export default SettingsHoverPopover;
